/*
Ilan's Website
Copyright (C) 2024-2026 ILAN ROSENBAUM

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

You should have received a copy of the GNU General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import React, { ReactNode, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useLocation, useNavigate } from "react-router-dom";
import BackButton from "./BackButton";
import RestaurantTable from "./RestaurantTable";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import DataviewTable from "./DataviewTable";
import { COLORS, Footer } from "../Constants";

interface MarkdownPageProps {
  source: string;
  backTo?: string;
  backButtonFill?: string;
  textColor?: string;
  linkColor?: string;
  googleSheetId?: string;
  googleSheetGids?: string[];
  useWideContainer?: boolean;
}

const MarkdownPage: React.FC<MarkdownPageProps> = ({
  source,
  backTo,
  backButtonFill,
  textColor,
  linkColor,
  googleSheetId,
  googleSheetGids,
  useWideContainer = false
}) => {
  const [markdown, setMarkdown] = useState<string>("");
  const [tableDatas, setTableDatas] = useState<any[][]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const location = useLocation();
  const navigate = useNavigate();
  const effectiveBackTo = (location.state as any)?.backTo || backTo;

  useEffect(() => {
    const fetchPublicGoogleSheetData = async (
      sheetId: string,
      gid: string
    ): Promise<any[]> => {
      const url = `https://docs.google.com/spreadsheets/d/${sheetId}/gviz/tq?tqx=out:csv&gid=${gid}`;
      const response = await fetch(url);
      const csvData = await response.text();
      return parseCSV(csvData);
    };

    const fetchMarkdownAndSheetData = async () => {
      try {
        const response = await fetch(source);
        let text = await response.text();

        setMarkdown(text);

        if (googleSheetId && googleSheetGids && googleSheetGids.length > 0) {
          const sheetDatas = await Promise.all(
            googleSheetGids.map(async (gid) => {
              return await fetchPublicGoogleSheetData(googleSheetId, gid);
            })
          );

          setTableDatas(sheetDatas);
        }

        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setIsLoading(false);
      }
    };

    fetchMarkdownAndSheetData();
  }, [source, googleSheetId, googleSheetGids]);

  const parseCSV = (csvText: string): any[] => {
    const rows = csvText.split("\n").map((row) => {
      const cells = [];
      let inQuotes = false;
      let currentCell = "";
      for (let i = 0; i < row.length; i++) {
        if (row[i] === '"') {
          inQuotes = !inQuotes;
        } else if (row[i] === "," && !inQuotes) {
          cells.push(currentCell.trim());
          currentCell = "";
        } else {
          currentCell += row[i];
        }
      }
      cells.push(currentCell.trim());
      return cells;
    });

    const headers = rows[0];
    return rows.slice(1).map((row) => {
      return headers.reduce((obj, header, index) => {
        obj[header.replace(/^"|"$/g, "")] =
          row[index]?.replace(/^"|"$/g, "") || "";
        return obj;
      }, {} as any);
    });
  };

  const components = {
    code({ node, inline, className, children, ...props }: any) {
      const match = /language-(\w+)/.exec(className || "");
      const content = String(children).replace(/\n$/, "");

      if (!inline && match && match[1] === "dataviewjs") {
        return <DataviewTable content={content} />;
      }

      return !inline && match ? (
        <SyntaxHighlighter
          style={vscDarkPlus}
          language={match[1]}
          PreTag="div"
          {...props}
        >
          {content}
        </SyntaxHighlighter>
      ) : (
        <code className={className} {...props}>
          {children}
        </code>
      );
    },
    table({ children }: any) {
      return (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse border border-gray-700 font-sans text-white text-sm sm:text-base">
            {children}
          </table>
        </div>
      );
    },

    thead({ children }: any) {
      return (
        <thead className="bg-gray-800 text-white font-sans">{children}</thead>
      );
    },

    th({ children }: any) {
      return (
        <th className="px-2 py-1 sm:px-4 sm:py-2 text-left whitespace-nowrap border-b border-gray-700 font-sans text-white text-xs sm:text-sm">
          {children}
        </th>
      );
    },

    td({ children }: any) {
      return (
        <td className="px-2 py-1 sm:px-4 sm:py-2 border-t border-gray-700 whitespace-nowrap font-sans text-white text-xs sm:text-sm">
          {children}
        </td>
      );
    },

    a({ href, children, ...props }: any) {
      const isInternal = href && (href.startsWith("/") || href.startsWith("#"));
      if (isInternal) {
        return (
          <a
            href={href}
            style={{ color: linkColor || COLORS.ACCENT_LINK_PURPLE }}
            onClick={(e) => { e.preventDefault(); navigate(href); }}
            {...props}
          >
            {children}
          </a>
        );
      }
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: linkColor || COLORS.ACCENT_LINK_PURPLE }} {...props}>
          {children}
        </a>
      );
    }
  };

  const renderContent = (): ReactNode => {
    if (!markdown) return null;

    const elements: ReactNode[] = [];
    const placeholderRegex = /{{GOOGLE_SHEETS_DATA_\d+}}/g;
    const parts = markdown.split(placeholderRegex);

    parts.forEach((part, index) => {
      if (part) {
        elements.push(
          <ReactMarkdown
            key={`md-${index}`}
            className="markdown font-mono text-[#ffebcd]"
            components={components}
            rehypePlugins={[rehypeRaw]}
            remarkPlugins={[remarkGfm]}
          >
            {part}
          </ReactMarkdown>
        );
      }

      if (index < parts.length - 1) {
        const placeholderMatch = markdown.match(placeholderRegex);
        const placeholder = placeholderMatch ? placeholderMatch[index] : null;
        const placeholderIndex = placeholder
          ? parseInt(placeholder.match(/\d+/)?.[0] || "1") - 1
          : 0;
        elements.push(
          isLoading ? (
            <div
              key={`loading-${index}`}
              className="flex justify-center items-center h-32"
            >
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
            </div>
          ) : (
            <RestaurantTable
              key={`table-${index}`}
              data={tableDatas[placeholderIndex] || []}
            />
          )
        );
      }
    });

    return elements;
  };

  return (
    <div className="h-screen w-screen bg-black/90 text-white overflow-hidden flex flex-col">
      {/* Fixed header with back button */}
      <div className="flex-shrink-0 p-4 sm:p-8">
        <BackButton
          textColor={textColor || COLORS.BACK_BUTTON_TEXT}
          color={backButtonFill || COLORS.BACK_BUTTON_PURPLE}
          to={effectiveBackTo || "/"}
        />
      </div>
      {/* Scrollable content area */}
      <div className="flex-1 overflow-auto min-h-0">
        <div
          className={`${
            useWideContainer ? "markdown-container-wide" : "markdown-container"
          }`}
        >
          {renderContent()}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default MarkdownPage;
